import { z } from 'zod';

/**
 * Stable column IDs for the conversation board. Spec v4 §boards.
 * The order matters — the array index in {@link OrgBoardConfigSchema}
 * is the left-to-right column order in the UI.
 */
export const CardColumnIdSchema = z.enum(['backlog', 'in_progress', 'resolved']);

export type CardColumnId = z.infer<typeof CardColumnIdSchema>;

/**
 * Stable column IDs for the task board. Spec v4 §boards.
 */
export const TaskColumnIdSchema = z.enum([
  'ai_handling',
  'awaiting_human',
  'human_handling',
  'resolved',
]);

export type TaskColumnId = z.infer<typeof TaskColumnIdSchema>;

const ColumnLabelSchema = z.string().min(1).max(24);

/**
 * Design-system tone for a board column's indicator dot. Shared by the
 * board UI and the org board config so the tenant-chosen colour persists
 * alongside the label. Spec mirrors the v2 mockup swatch palette.
 */
export const ColumnToneSchema = z.enum(['gray', 'brand', 'green', 'amber', 'red', 'ai']);

export type ColumnTone = z.infer<typeof ColumnToneSchema>;

export const CardColumnConfigSchema = z.object({
  id: CardColumnIdSchema,
  label: ColumnLabelSchema,
  tone: ColumnToneSchema,
});

export type CardColumnConfig = z.infer<typeof CardColumnConfigSchema>;

export const TaskColumnConfigSchema = z.object({
  id: TaskColumnIdSchema,
  label: ColumnLabelSchema,
});

export type TaskColumnConfig = z.infer<typeof TaskColumnConfigSchema>;

/**
 * How long resolved cards stay visible on the board before dropping off
 * (they remain in history). Org-level setting from the v2 mockup.
 */
export const ResolvedRetentionSchema = z.enum(['8h', '12h', '24h', '48h', '7d']);

export type ResolvedRetention = z.infer<typeof ResolvedRetentionSchema>;

/** Default retention for resolved cards — matches the v2 mockup. */
export const DEFAULT_RESOLVED_RETENTION: ResolvedRetention = '24h';

/** Resolved-retention window expressed in milliseconds. Used to compute
 * the `closedAt` cutoff below which a resolved card still counts as
 * "recent" (board resolved column + sidebar counts). */
export function resolvedRetentionToMs(retention: ResolvedRetention): number {
  const HOUR = 3_600_000;
  switch (retention) {
    case '8h':
      return 8 * HOUR;
    case '12h':
      return 12 * HOUR;
    case '24h':
      return 24 * HOUR;
    case '48h':
      return 48 * HOUR;
    case '7d':
      return 7 * 24 * HOUR;
    default: {
      const exhaustive: never = retention;
      return exhaustive;
    }
  }
}

const CARD_COLUMN_IDS: readonly CardColumnId[] = ['backlog', 'in_progress', 'resolved'];
const TASK_COLUMN_IDS: readonly TaskColumnId[] = [
  'ai_handling',
  'awaiting_human',
  'human_handling',
  'resolved',
];

/**
 * Per-tenant board configuration. The config holds the labels (display
 * strings) for each column; the column IDs are code-level constants
 * (see `CardColumnIdSchema` / `TaskColumnIdSchema`) so the board
 * derivation in Phase 2 stays decoupled from any one tenant's choice
 * of words.
 *
 * Validation rules: every column ID must appear exactly once, in the
 * canonical order. The MVP forbids reordering / adding / removing
 * columns — only the labels are tenant-configurable.
 */
export const OrgBoardConfigSchema = z.object({
  cardColumns: z
    .array(CardColumnConfigSchema)
    .length(CARD_COLUMN_IDS.length)
    .refine(
      (cols) => CARD_COLUMN_IDS.every((id, idx) => cols[idx]?.id === id),
      `cardColumns must contain exactly [${CARD_COLUMN_IDS.join(', ')}] in order`,
    ),
  taskColumns: z
    .array(TaskColumnConfigSchema)
    .length(TASK_COLUMN_IDS.length)
    .refine(
      (cols) => TASK_COLUMN_IDS.every((id, idx) => cols[idx]?.id === id),
      `taskColumns must contain exactly [${TASK_COLUMN_IDS.join(', ')}] in order`,
    ),
  resolvedRetention: ResolvedRetentionSchema.default(DEFAULT_RESOLVED_RETENTION),
  updatedAt: z.coerce.date(),
  updatedByUserId: z.string().min(1).max(200),
});

export type OrgBoardConfig = z.infer<typeof OrgBoardConfigSchema>;

/**
 * Portuguese default labels — match the design handoff bundle
 * (`/tmp/voll-design/voll-intelligence-oficial-mvp/project/v2/kanban-visoes.jsx`).
 * Used by `reset-dev-db.ts` to seed a baseline config per known org.
 */
export const DEFAULT_CARD_COLUMNS: readonly CardColumnConfig[] = [
  { id: 'backlog', label: 'Backlog', tone: 'gray' },
  { id: 'in_progress', label: 'Executando', tone: 'brand' },
  { id: 'resolved', label: 'Resolvido', tone: 'green' },
];

export const DEFAULT_TASK_COLUMNS: readonly TaskColumnConfig[] = [
  { id: 'ai_handling', label: 'IA conduzindo' },
  { id: 'awaiting_human', label: 'Aguardando time' },
  { id: 'human_handling', label: 'Em execução' },
  { id: 'resolved', label: 'Resolvido' },
];
